import { Carousel } from "@ui/molecules/Carousel/Carousel";
import {
	type Accessor,
	createEffect,
	createMemo,
	createSignal,
	For,
	type JSX,
	Show,
} from "solid-js";
import "./VideoCarousel.css";

export type VideoCarouselSlideContext = {
	index: number;
	isPortrait: boolean;
	isActive: boolean;
	/**
	 * Opens this slide. The slide itself is inert, so its content carries the
	 * control — a slide holding controls of its own could not be a button
	 * without nesting them inside one.
	 */
	activate: (event: MouseEvent) => void;
};

export type VideoCarouselProps<T> = {
	items: readonly T[];
	renderSlide: (item: T, ctx: VideoCarouselSlideContext) => JSX.Element;
	startIndex?: number;
	scrollMode?: "buttons" | "mousewheel";
	activeSlideIndex?: Accessor<number | null>;
	onSlideClick?: (index: number, event: MouseEvent) => void;
	class?: string;
	overlay?: JSX.Element;
};

export function getVideoAspectRatio(item: {
	aspect_ratio?: number | null;
	aspectRatio?: number | null;
}) {
	return item.aspect_ratio ?? item.aspectRatio ?? 1;
}

export function areAllPortraitVideos(
	items: readonly { aspect_ratio?: number | null }[],
) {
	return (
		items.length > 0 && items.every((item) => getVideoAspectRatio(item) <= 1)
	);
}

type InlineSlideUnit<T> =
	| { kind: "single"; index: number; item: T }
	| {
			kind: "landscape-stack";
			indices: [number, number];
			items: [T, T];
	  };

function buildInlineSlideUnits<T>(
	items: readonly T[],
	isLandscape: (item: T) => boolean,
): InlineSlideUnit<T>[] {
	const units: InlineSlideUnit<T>[] = [];
	let i = 0;
	while (i < items.length) {
		const item = items[i];
		const next = items[i + 1];
		if (
			item !== undefined &&
			next !== undefined &&
			isLandscape(item) &&
			isLandscape(next)
		) {
			units.push({
				kind: "landscape-stack",
				indices: [i, i + 1],
				items: [item, next],
			});
			i += 2;
		} else if (item !== undefined) {
			units.push({ kind: "single", index: i, item });
			i += 1;
		}
	}
	return units;
}

/**
 * Now used only for the inline chat carouse.
 */
export const VideoCarousel = <T,>(props: VideoCarouselProps<T>) => {
	const [activeSlide, setActiveSlide] = createSignal<number | null>(
		props.startIndex ?? 0,
	);

	createEffect(() => {
		const external = props.activeSlideIndex?.();
		if (external !== null && external !== undefined) {
			setActiveSlide(external);
		}
	});

	const isLandscapeItem = (item: T) =>
		getVideoAspectRatio(
			item as { aspect_ratio?: number; aspectRatio?: number },
		) > 1;
	const inlineUnits = createMemo(() =>
		buildInlineSlideUnits(props.items, isLandscapeItem),
	);
	const renderSlideContent = (item: T, index: number) => {
		const aspectRatio = getVideoAspectRatio(
			item as { aspect_ratio?: number; aspectRatio?: number },
		);
		const isPortrait = aspectRatio <= 1;
		const isActive = activeSlide() === index;
		return props.renderSlide(item, {
			index,
			isPortrait,
			isActive,
			activate: (event) => {
				props.onSlideClick?.(index, event);
			},
		});
	};

	return (
		<div
			class={`gvtui-video-carousel-shell gvtui-video-carousel-shell--object-contain ${props.class ?? ""}`}
		>
			<Carousel
				scrollMode={props.scrollMode ?? "buttons"}
				layout="standard"
				startIndex={props.startIndex}
				scrollType="snap"
				onActiveRealSlideChange={(index) => {
					setActiveSlide(index);
				}}
			>
				<For each={inlineUnits()}>
					{(unit) => {
						if (unit.kind === "landscape-stack") {
							return (
								<div class="gvtui-video-carousel__slide gvtui-video-carousel__slide--stack">
									<div class="gvtui-video-carousel__stack-item">
										{renderSlideContent(unit.items[0], unit.indices[0])}
									</div>
									<div class="gvtui-video-carousel__stack-item">
										{renderSlideContent(unit.items[1], unit.indices[1])}
									</div>
								</div>
							);
						}
						return (
							<div class="gvtui-video-carousel__slide">
								{renderSlideContent(unit.item, unit.index)}
							</div>
						);
					}}
				</For>
			</Carousel>
			<Show when={props.overlay}>{props.overlay}</Show>
		</div>
	);
};
